import { useState } from "react";
import { toast } from "react-toastify";

// COMPONENTS
import KOGFileUploader from "@/main/components/shared/fileUploader/fileUploader";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";

// SERVICES/MODELS/ENUM
import { getTranslateByKey, isShowControlByElementId } from "@/main/services/utils";
import { quoteApi } from "@/main/api";
import { ImportQuoteFileHistoryRequest } from "@/main/models/request";
import { useModalReducer } from "@/main/store/modal";
import { ModalEnum } from "@/main/services/enum/ModalEnum";
import { Controls } from "@/main/services/constants/constantsControl";
import FileHistory from "./fileHistory/fileHistoryGrid";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { ImportQuoteFileHistoryResponse } from "@/main/models/response";
import * as S from "./styles";

interface IModalImportQuoteProps {
	isOpenModal: boolean;
	handleModalImportQuote: () => void;
	handleOpenModalImportQuote: () => void;
	loadInitialData: (ignoreInitialLoadScreen: boolean) => void;
}

function ModalImportQuote({
	isOpenModal,
	handleModalImportQuote,
	handleOpenModalImportQuote,
	loadInitialData,
}: IModalImportQuoteProps) {
	// Reducers
	const modalReducer = useModalReducer();
	// States
	const [files, setFiles] = useState<File[]>([]);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	const [dataFileHistory, setDataFileHistory] = useState<ImportQuoteFileHistoryResponse[]>([]);
	const [formFiltersFileHistory, setFormFiltersFileHistory] = useState<ImportQuoteFileHistoryRequest | null>(null);

	function onResetFileHistory() {
		setDataFileHistory([]);
		setFormFiltersFileHistory(null);
	}
	// Async Functions
	async function getDataOrderHistory(filters: ImportQuoteFileHistoryRequest = {}) {
		const request: ImportQuoteFileHistoryRequest = {
			...filters,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize10,
		};
		onResetFileHistory();
		setFormFiltersFileHistory(request);
	}

	async function handleImport() {
		try {
			if (files.length === 0) return toast.error(getTranslateByKey("information.RequiredFile", true));
			setIsLoadingSave(true);

			await quoteApi.importQuoteFiles(files);

			const removeConvertedFiles = files.filter(
				(file) => !files.some((convertedFile) => convertedFile.name === file.name),
			);

			setFiles(removeConvertedFiles);
			toast.success(getTranslateByKey("information.importQuoteSuccessfully"));
			getDataOrderHistory();
			loadInitialData(true);
		} catch {
		} finally {
			setIsLoadingSave(false);
		}
	}

	function handleBack() {
		handleModalImportQuote();
	}

	const conflictingDragDropModals = [
		ModalEnum.InvoiceManagement,
		ModalEnum.ModalFade,
		ModalEnum.ModalCancelReturn,
		ModalEnum.ModalChangeStatusItem,
		ModalEnum.Default,
	];

	const hasConflictingModalOpen =
		modalReducer.open && conflictingDragDropModals.find((modalName) => modalName === modalReducer.name);
	const enableDragDropFullScreen = isShowControlByElementId(Controls.IdImportQuote) && !hasConflictingModalOpen;
	return (
		<S.Container>
			<h4>{getTranslateByKey("information.importQuote")}</h4>

			<KOGFileUploader
				isFullScreenDragDrop={enableDragDropFullScreen}
				files={files}
				setFiles={setFiles}
				maxFiles={100}
				typeFiles={["json", "pdf", "xlsx"]}
				onCustomDropAction={handleOpenModalImportQuote}
			/>

			<FileHistory
				isOpenModal={isOpenModal}
				dataFileHistory={dataFileHistory}
				formFiltersFileHistory={formFiltersFileHistory}
				setDataFileHistory={setDataFileHistory}
				getDataOrderHistory={getDataOrderHistory}
			/>
			<S.Footer>
				<SecondaryButton id="id-importQuote-btn-back" onClick={handleBack}>
					{getTranslateByKey("information.back")}
				</SecondaryButton>
				<SecondaryButton id="id-importQuote-btn-import" isLoading={isLoadingSave} onClick={handleImport}>
					{getTranslateByKey("information.import")}
				</SecondaryButton>
			</S.Footer>
		</S.Container>
	);
}

export default ModalImportQuote;
